import React from 'react';
import { FaCheckCircle, FaTimesCircle, FaShoppingCart, FaWhatsapp, FaGlobe, FaLock, FaUserCheck, FaCreditCard, FaEnvelope, FaPhone, FaArrowRight, FaRocket, FaChartBar, FaShieldAlt, FaUserPlus, FaKey } from 'react-icons/fa';
import { motion } from 'framer-motion';
import  wcAccessicon  from '../../assets/icons/8991273.png';

const WhoCanAccess = ({ contentData }) => {
  return (
    <section className="relative py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute top-20 left-10 w-64 h-64 border-4 border-emerald-500/30 rounded-full"
        />
        
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-20 right-10 w-80 h-80 border-4 border-blue-500/30 rounded-full"
        />
      </div>

      {/* Floating Icons */}
      <motion.div
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
        className="absolute top-20 left-5 text-4xl opacity-10"
      >
        <img src={wcAccessicon} className="w-50 opacity-50" alt="lock icon" height={50} width={50} />
      </motion.div>
      <motion.div
        animate={{ y: [0, 15, 0] }}
        transition={{ duration: 5, repeat: Infinity, delay: 1 }}
        className="absolute top-1/3 right-8 text-5xl opacity-10"
      >
        📈
      </motion.div>
      <motion.div
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 6, repeat: Infinity, delay: 0.5 }}
        className="absolute bottom-1/4 left-8 text-6xl opacity-10"
      >
        💰
      </motion.div>
      <motion.div
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 7, repeat: Infinity, delay: 1.5 }}
        className="absolute bottom-1/3 right-5 text-4xl opacity-10"
      >
        🎯
      </motion.div>

      {/* Animated Lines */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        <motion.path
          d="M100,100 Q250,50 400,150 Q550,250 700,100"
          fill="none"
          stroke="#10b981"
          strokeWidth="2"
          strokeDasharray="5,5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, repeat: Infinity }}
        />
        <motion.path
          d="M200,300 Q400,200 600,300 Q800,400 1000,250"
          fill="none"
          stroke="#3b82f6"
          strokeWidth="2"
          strokeDasharray="5,5"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
        />
      </svg>

      {/* Floating Access Cards */}
      <motion.div
        animate={{ y: [0, -30, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
        className="absolute top-20 right-16 hidden lg:block w-32 h-20 bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 backdrop-blur-sm rounded-xl shadow-lg border border-emerald-500/20"
      >
        <div className="p-4">
          <div className="flex items-center space-x-2">
            <FaLock className="text-emerald-400 text-sm" />
            <span className="text-emerald-300 text-xs font-bold">ACCESS</span>
          </div>
          <div className="mt-2 flex space-x-1">
            {[1, 2, 3].map((i) => (
              <div key={i} className="w-3 h-3 bg-emerald-400/30 rounded-full"></div>
            ))}
          </div>
        </div>
      </motion.div>

      <motion.div
        animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
        transition={{ duration: 7, repeat: Infinity, delay: 2 }}
        className="absolute bottom-32 right-20 hidden lg:block w-36 h-24 bg-gradient-to-r from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-xl shadow-lg border border-purple-500/20"
      >
        <div className="p-4">
          <div className="flex items-center space-x-2">
            <FaUserCheck className="text-purple-400 text-sm" />
            <span className="text-purple-300 text-xs font-bold">MEMBERSHIP</span>
          </div>
          <div className="mt-2 flex space-x-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-4 h-4 bg-gradient-to-r from-purple-400/20 to-pink-400/20 rounded"></div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Animated Dots Grid */}
      <div className="absolute top-10 right-1/4 hidden xl:grid grid-cols-3 gap-2 opacity-10">
        {[...Array(9)].map((_, i) => (
          <motion.div
            key={i}
            animate={{ scale: [1, 1.5, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
            className="w-3 h-3 bg-emerald-500 rounded-full"
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 backdrop-blur-sm border border-blue-400/30 px-6 py-3 rounded-full mb-6 mx-auto shadow-lg">
            <FaShieldAlt className="text-blue-400" />
            <span className="text-blue-300 font-bold text-sm">SECURE ACCESS PORTAL</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Who Can Access This Portal?
          </h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Exclusive access for premium members only
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Authorized Members */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-8 rounded-2xl shadow-lg border border-emerald-500/20 overflow-hidden backdrop-blur-sm"
            >
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
              </div>

              {/* Floating Icon Group */}
              <div className="absolute top-4 right-4 flex space-x-2">
                {[FaLock, FaUserCheck, FaCreditCard, FaEnvelope].map((Icon, idx) => (
                  <motion.div
                    key={idx}
                    animate={{ y: [0, -8, 0] }}
                    transition={{ duration: 2, repeat: Infinity, delay: idx * 0.3 }}
                    className="w-8 h-8 bg-gray-700/50 rounded-lg flex items-center justify-center backdrop-blur-sm"
                  >
                    <Icon className="text-emerald-400 text-sm" />
                  </motion.div>
                ))}
              </div>

              <div className="flex items-center gap-3 mb-6 relative z-10">
                <motion.div
                  className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center"
                >
                  <FaCheckCircle className="text-white text-2xl" />
                </motion.div>
                <h3 className="text-2xl font-bold text-white">
                  {contentData?.sap_left_card_title_main}
                </h3>
              </div>

              <div className="space-y-6 relative z-10">
                {[
                  {
                    title: contentData?.sap_left_card_title1,
                    desc: contentData?.sap_left_card_info1,
                    icon: <FaChartBar className="text-emerald-400" />
                  },
                  {
                    title: contentData?.sap_left_card_title2,
                    desc: contentData?.sap_left_card_info2,
                    icon: <FaCreditCard className="text-emerald-400" />
                  },
                  {
                    title: contentData?.sap_left_card_title3,
                    desc: contentData?.sap_left_card_info3,
                    icon: <FaEnvelope className="text-emerald-400" />
                  }
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-all duration-300 cursor-pointer backdrop-blur-sm"
                  >
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                      className="w-10 h-10 bg-gradient-to-br from-gray-700/50 to-gray-800/50 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm backdrop-blur-sm"
                    >
                      {item.icon}
                    </motion.div>
                    <div className="flex-1">
                      <h4 className="font-bold text-white flex items-center gap-2">
                        {item.title}
                        <motion.div
                          animate={{ opacity: [0, 1, 0] }}
                          transition={{ duration: 1.5, repeat: Infinity, delay: index * 0.3 }}
                          className="w-2 h-2 bg-emerald-500 rounded-full"
                        />
                      </h4>
                      <p className="text-gray-300 text-sm mt-1">{item.desc}</p>
                    </div>
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      className="text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <FaArrowRight />
                    </motion.div>
                  </motion.div>
                ))}
              </div>

              {/* Animated Success Badge */}
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 backdrop-blur-sm rounded-full border border-emerald-500/30"
              >
                <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                <span className="text-emerald-300 font-semibold" style={{fontSize: "10px"}}>Access Granted</span>
              </motion.div>
            </motion.div>
          </div>

          {/* Not a Member Yet */}
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="relative bg-gradient-to-br from-gray-800/60 to-gray-900/60 p-8 rounded-2xl shadow-lg border border-red-500/20 overflow-hidden backdrop-blur-sm"
            >
              {/* Animated Background Pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 left-0 w-32 h-32 bg-red-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl"></div>
              </div>

              {/* Floating Countdown */}
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute top-6 right-6 w-16 h-16 border-4 border-red-400/30 border-t-transparent rounded-full flex items-center justify-center"
              >
                <span className="text-red-300 font-bold text-xs">JOIN</span>
              </motion.div>

              <div className="flex items-center gap-3 mb-6 relative z-10">
                <motion.div
                  className="w-12 h-12 bg-gradient-to-r from-red-500 to-orange-600 rounded-xl flex items-center justify-center"
                >
                  <FaTimesCircle className="text-white text-2xl" />
                </motion.div>
                <h3 className="text-2xl font-bold text-white">
                   Not a Member Yet?
                </h3>
              </div>

              <p className="text-gray-300 mb-6 relative z-10">
                If you haven't purchased our research plan, you cannot access this portal. 
                Subscribe now to get exclusive access to premium research.
              </p>

              <div className="space-y-4 relative z-10">
                {[
                  {
                    icon: FaWhatsapp,
                    color: "bg-emerald-500",
                    title: "To Purchase Membership:",
                    link: "tel:9319745997",
                    value: "9319745997",
                    bg: "bg-gray-700/30",
                    hover: "hover:bg-gray-700/50",
                    text: "text-emerald-400"
                  },
                  {
                    icon: FaGlobe,
                    color: "bg-purple-500",
                    title: "Main Website:",
                    link: "https://www.marketmaestroo.com",
                    value: "www.marketmaestroo.com",
                    bg: "bg-gray-700/30",
                    hover: "hover:bg-gray-700/50",
                    text: "text-purple-400"
                  }
                ].map((item, index) => (
                  <motion.a
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 + (index * 0.1) }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.02, y: -3 }}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 p-4 ${item.bg} rounded-lg ${item.hover} transition-all duration-300 group shadow-sm hover:shadow-md backdrop-blur-sm`}
                  >
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, delay: index * 0.5 }}
                      className={`w-10 h-10 ${item.color} rounded-lg flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg`}
                    >
                      <item.icon className="text-white text-lg" />
                    </motion.div>
                    <div className="flex-1">
                      <p className="font-bold text-white">{item.title}</p>
                      <p className={`${item.text} font-semibold group-hover:underline`}>
                        {item.value}
                      </p>
                    </div>
                    <motion.div
                      initial={{ x: 0 }}
                      whileHover={{ x: 5 }}
                      className="text-gray-500 group-hover:text-gray-300"
                    >
                      <FaArrowRight />
                    </motion.div>
                  </motion.a>
                ))}
              </div>

              {/* Animated Subscribe Button */}
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="mt-6"
              >
                <button className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-500 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 group">
                  <FaRocket className="group-hover:rotate-12 transition-transform" />
                  Subscribe Now & Get Access
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="inline-block"
                  >
                    →
                  </motion.span>
                </button>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Floating Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-12 text-center"
        >
          <div className="inline-flex items-center gap-4 bg-gradient-to-r from-gray-800/60 to-gray-900/60 backdrop-blur-sm border border-gray-700 px-6 py-4 rounded-2xl shadow-lg">
            <FaKey className="text-gray-400" />
            <span className="text-gray-300 font-medium">
              Need help with access? Contact support at{" "}
              <span className="text-blue-400 font-bold">9319745997</span>
            </span>
          </div>
        </motion.div>
      </div>

      {/* Floating Progress Bars */}
      <div className="absolute bottom-10 left-10 hidden xl:block w-48 opacity-20">
        <div className="mb-2">
          <span className="text-gray-400 text-xs">Access Rate</span>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "85%" }}
              transition={{ duration: 2 }}
              className="h-full bg-gradient-to-r from-emerald-500 to-teal-500"
            />
          </div>
        </div>
        <div>
          <span className="text-gray-400 text-xs">Membership Growth</span>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "72%" }}
              transition={{ duration: 2, delay: 0.5 }}
              className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhoCanAccess;